import makeLogger from "../shared/lib/log";
import { getTime } from "./common";
import { getFbp } from "./facebook";
import { Handshake, handshake } from "./handshake";
import { InteractionRecorder } from "./interaction";
import { getLiveIntentSha, whitelistDomain } from "./liveintent";
import makeRequester, { Requester } from "./requester";
import { RRWebRecorder } from "./rrweb";
import { get, put } from "./session";
import { ShopifyCart } from "./shopify";
import { Event, TrackData, track } from "./visitor";

const logger = makeLogger("ai.roas.pixel");

interface IdentityData {
  email: string;
}

class Pixel {
  private loaded = false;

  private interactionRecorder: InteractionRecorder = new InteractionRecorder();
  private rrwebRecorder: RRWebRecorder = new RRWebRecorder();
  private requester: Requester | undefined;
  private settings: Handshake | undefined;
  private identity: IdentityData | undefined;
  private hem: string | undefined;

  private requesterPromise: Promise<void>;
  private requesterReady: () => void;

  private lingerStart: number = getTime();
  private lingerPromise: Promise<void> | undefined;
  private lingerInterval: number | undefined;

  private visitorID: string | undefined;
  private sessionID: string | undefined;
  private transactionID: string | undefined;

  private rrwebEvents: Record<string, any>[] = [];
  private rrwebInterval: number | undefined;

  private interactionsInterval: number | undefined;

  constructor() {
    this.visitorID = get("visitor-id");
    this.sessionID = get("session-id", "session");
    this.requesterPromise = new Promise((resolve) => {
      this.requesterReady = resolve;
    });
  }

  async load(key: string): Promise<void> {
    logger.debug(`load`, { key });
    if (this.loaded) {
      logger.warn("load: already loaded");
      return;
    }
    this.requester = makeRequester(key);
    this.requesterReady();
    this.loaded = true;
    this.loadHem(); // don't await
    await this.handshake();
    await this.trackLoad();
    await this.updateShopifyCart();
    window.addEventListener("beforeunload", () => this.handleUnload());
    document.addEventListener("visibilitychange", () => this.handleVisChange());
  }

  async handshake() {
    try {
      logger.debug("handshake");
      this.settings = await handshake(this.requester);
    } catch (error) {
      logger.error("handshake failed", { error });
    }
  }

  async identify(identity: string | IdentityData) {
    logger.debug("identify", { identity });
    if (typeof identity === "string") {
      identity = { email: identity };
    }
    this.identity = identity;
    await this.track({
      event: Event.Identify,
      email: identity.email,
      url: document.location.href,
    });
  }

  private async loadHem(): Promise<void> {
    const domain =
      window.location.hostname === "127.0.0.1"
        ? "127.0.0.1"
        : window.location.hostname.split(".").slice(-2).join(".");
    logger.debug("whitelisting domain", { domain });
    await whitelistDomain(this.requester, domain);
    try {
      this.hem = await getLiveIntentSha();
      logger.debug("received LiveIntent sha", { hem: this.hem });
    } catch (error) {
      logger.error("LiveIntent failed", { error });
    }
  }

  private async trackLoad(): Promise<void> {
    await this.track({
      event: Event.PageLoad,
      email: this.identity?.email,
      fbp: getFbp(),
      hem: this.hem,
      url: document.location.href,
    });
    this.scheduleLinger();
    if (this.settings?.["interactions-enabled?"]) {
      this.startRecordingInteractions();
    }
    if (this.settings?.["rrweb-enabled?"]) {
      this.startRecordingRrweb();
    }
  }

  // invariant: called after a successful load[&identify]
  private scheduleLinger() {
    window.clearInterval(this.lingerInterval);
    this.lingerInterval = setInterval(() => {
      if (this.lingerPromise !== undefined) return;
      this.lingerPromise = this.linger();
    }, 5 * 1000);
  }

  private async startRecordingInteractions() {
    try {
      logger.debug("startRecordingInteractions");
      this.interactionRecorder.record();
      window.clearInterval(this.interactionsInterval);
      this.interactionsInterval = setInterval(
        () => this.flushInteractions(),
        5000,
      );
    } catch (error) {
      logger.error("interactions failed", { error });
    }
  }

  private async flushInteractions() {
    const events = await this.interactionRecorder.flush();
    if (events.length === 0) {
      return;
    }
    try {
      logger.debug("flushInteractions: flushing interactions");
      await this.track({
        event: Event.Interactions,
        email: this.identity?.email,
        hem: this.hem,
        url: document.location.href,
        data: {
          events,
        },
      });
    } catch (error) {
      logger.error("flushInteractions: failed to flush", { error });
    }
  }

  private async startRecordingRrweb() {
    try {
      logger.debug("startRecordingRrweb");
      await this.rrwebRecorder.record((data) => {
        logger.debug("RRWeb event", { data });
        this.rrwebEvents.push(data);
        if (this.rrwebEvents.length >= 1000) {
          this.flushRecording();
        }
      });
      window.clearInterval(this.rrwebInterval);
      this.rrwebInterval = setInterval(() => this.flushRecording(), 5000);
    } catch (error) {
      logger.error("RRWeb failed", { error });
    }
  }

  private async flushRecording() {
    const events = [...this.rrwebEvents];
    this.rrwebEvents = [];
    if (events.length === 0) {
      return;
    }
    try {
      logger.debug("flushRecording: flushing rrwebEvents");
      await this.track({
        event: Event.RRWeb,
        email: this.identity?.email,
        hem: this.hem,
        url: document.location.href,
        data: {
          events,
        },
      });
    } catch (error) {
      logger.error("flushRecording: failed to flush", { error });
    }
  }

  private async linger(): Promise<void> {
    try {
      const now = getTime();
      const duration = now - this.lingerStart;
      if (duration < 1000) {
        return;
      }
      logger.debug("linger", { duration });
      await this.track({
        event: Event.Linger,
        email: this.identity?.email,
        hem: this.hem,
        url: document.location.href,
        data: {
          duration: duration,
        },
      });
      this.lingerStart = now;
    } catch (error) {
      logger.error("failed to linger", { error });
    } finally {
      this.lingerPromise = undefined;
    }
  }

  private async track(data: TrackData): Promise<void> {
    await this.requesterPromise;
    try {
      const { vid, sid, xid } = await track(this.requester, {
        ...data,
        vid: this.visitorID,
        sid: this.sessionID,
        xid: this.transactionID,
      });
      this.visitorID = vid;
      this.sessionID = sid;
      if (this.transactionID === undefined) {
        this.transactionID = xid;
      }
      put("visitor-id", vid);
      put("session-id", sid, "session");
    } catch (error) {
      logger.error("failed to track", { error });
    }
  }

  private async updateShopifyCart(): Promise<void> {
    if (!this.visitorID) return;
    if (!this.sessionID) return;
    const cart = new ShopifyCart();
    await cart.setAttributes({
      __roas_visitor_id: this.visitorID,
      __roas_session_id: this.sessionID,
    });
  }

  private handleUnload() {
    logger.debug("handling unload");
    if (this.settings?.["interactions-enabled?"]) {
      this.flushInteractions();
    }
    if (this.settings?.["rrweb-enabled?"]) {
      this.flushRecording();
    }
  }

  private handleVisChange() {
    logger.debug("handling visibility change");
    switch (document.visibilityState) {
      case "visible":
        logger.debug("resetting linger start");
        this.lingerStart = getTime();
        break;
      case "hidden":
        if (this.settings?.["interactions-enabled?"]) {
          this.flushInteractions();
        }
        if (this.settings?.["rrweb-enabled?"]) {
          this.flushRecording();
        }
        break;
    }
  }
}

const pixel = new Pixel();
((window as any).RoasAI?.q || []).forEach(([method, args]: [string, any[]]) => {
  logger.debug(`queue '${method}'`, args);
  (pixel as any)[method].apply(pixel, args);
});
(window as any).RoasAI = pixel;
